import { useMemo } from 'react';

import { Action, ActionBuilderContext, ADMISSION_CARD_TYPE, AdmissionCardActionsConfig } from './type';

/**
 * Action builder for Pass cards
 */
const buildPassActions = (context: ActionBuilderContext): Action[] => {
  const { actions, config, t } = context;
  const { isPreset, isToggleActive, onDelete, onEdit, isSharePaymentLinkActive, deleteIsUnassign } = config;
  const { handleAddHolder, handleDelete, handleEdit, handleViewHolders, handleShareLink } = config;

  // Add holder (only for presets)
  if (isPreset) {
    actions.push({
      name: t('passes:addHolderButton'),
      action: handleAddHolder,
    });
  }

  // Delete/Unassign (only if toggle is active)
  if (onDelete && isToggleActive) {
    actions.push({
      name: t(`common:${deleteIsUnassign ? 'unassign' : 'delete'}`),
      action: handleDelete,
    });
  }

  // Edit (only if toggle is active)
  if (onEdit && isToggleActive) {
    actions.push({
      name: t('common:edit'),
      action: handleEdit,
    });
  }

  // View holders (always available)
  actions.push({
    name: t('common:viewHolders'),
    action: handleViewHolders,
  });

  // Share payment link
  if (isSharePaymentLinkActive) {
    actions.push({
      name: t('common:sharePaymentLink'),
      action: handleShareLink,
    });
  }

  return actions;
};

/**
 * Action builder for Regular admission cards
 */
const buildRegularAdmissionActions = (context: ActionBuilderContext): Action[] => {
  const { actions, config, t } = context;
  const { isPreset, isPastEvent, isOpenForAll, deleteIsUnassign } = config;
  const { handleEdit, handleDuplicate, handleDelete, handleShareLink, handleUnassign } = config;

  // Edit (always first)
  actions.push({
    name: t('common:edit'),
    action: handleEdit,
  });

  // Duplicate (not for presets)
  if (!isPreset) {
    actions.push({
      name: t('common:duplicate'),
      action: handleDuplicate,
    });
  }

  // Delete (not for past events)
  if (!isPastEvent) {
    actions.push({
      name: t('common:delete'),
      action: handleDelete,
    });
  }

  // Share payment link (only if open for all)
  if (isOpenForAll) {
    actions.push({
      name: t('common:sharePaymentLink'),
      action: handleShareLink,
    });
  }

  // Unassign (if applicable)
  if (deleteIsUnassign) {
    actions.push({
      name: t('common:unassign'),
      action: handleUnassign,
    });
  }

  return actions;
};

/**
 * Extensible action builders registry
 * Add new admission types here in the future
 */
const ACTION_BUILDERS: Record<AdmissionCardActionsConfig['type'], (context: ActionBuilderContext) => Action[]> = {
  [ADMISSION_CARD_TYPE.pass]: buildPassActions,
  [ADMISSION_CARD_TYPE.offer]: buildRegularAdmissionActions,
  // Future types can be added here:
  // 'vip': buildVipAdmissionActions,
  // 'early-bird': buildEarlyBirdAdmissionActions,
  // etc.
};

/**
 * Custom hook to build admission card actions based on type and conditions
 *
 * @param config - Configuration object with card type, conditions, and handlers
 * @returns Array of actions to display in the submenu
 */
export const useAdmissionCardActions = (config: AdmissionCardActionsConfig): Action[] => {
  return useMemo(() => {
    const { type, t } = config;
    const builder = ACTION_BUILDERS[type];

    if (!builder) {
      console.warn(`No action builder found for type: ${type}`);
      return [];
    }

    const context: ActionBuilderContext = {
      actions: [],
      config,
      t,
    };

    return builder(context);
  }, [config]);
};
